import { ChangeDetectorRef, Component, ElementRef, HostListener, Inject, inject, Injector, runInInjectionContext, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ToastrService } from 'ngx-toastr';
import { AgGridAngular } from 'ag-grid-angular';
import type { ColDef } from 'ag-grid-community';
import { MatTabsModule } from '@angular/material/tabs';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { AbstractControl, FormControl, FormGroup, FormsModule, NgForm, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { ConfirmDialog } from '../../shared/components/confirm-dialog/confirm-dialog';
import { RolesService } from '../../features/roles/roles.service';
import { debounceTime, distinctUntilChanged, Subject } from 'rxjs';
import { Store } from '@ngrx/store';
import { PermissionDirective } from '../../shared/components/directives/permissions.directives';
import { take } from 'rxjs';
import {
  NoOnlySpacesDirective,
  NoOnlySpecialAlphaCharsDirective,
} from '../../core/helpers/validator.directive';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogActions,
  MatDialogClose,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle,
} from '@angular/material/dialog';
import * as selectPermissions from '../../core/permissions/permission.selector';
import { UserService } from '../../features/users/users.service';
import { MatButtonModule } from '@angular/material/button';
import { FullscreenLoaderComponent } from '../../shared/components/loader-component/loader.component';
import { AuthService } from '../../core/services/auth.service';
import { UserProfileService } from '../../features/users/profile.service';
import { noOnlySpacesValidator } from '../../core/helpers/validator';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { Storage, getStorage } from '@angular/fire/storage';

@Component({
  selector: 'app-roles',
  imports: [
    AgGridAngular,
    CommonModule,
    MatTabsModule,
    FormsModule,
    PermissionDirective
  ],
  templateUrl: './roles.component.html',
  styleUrl: './roles.component.scss',
})
export class RolesComponent {
  readonly dialog = inject(MatDialog);
  public viewPortSize: boolean = false;
  public rowData: any[] | null = null;
  public rolesData: any[] | null = null;
  public defaultColDef: ColDef = {
    filter: 'agTextColumnFilter',
    floatingFilter: true,
  };
  public rowSelection: 'single' | 'multiple' = 'multiple';
  public paginationPageSize = 10;
  public paginationPageSizeSelector: number[] | boolean = [10, 25, 50];
  public themeClass: string = 'ag-theme-quartz';
  private searchSubject = new Subject<string>();
  searchTerm: string = '';
  columnDefs: any[] = [];
  userDefs: any[] = [];
  users: any[] = [];
  canEditOrDelete: boolean = false;
  portalUsers: any;
  userPageSize: number = 10;
  userPage: number = 0;
  userTotalPages = 1;
  userPageSizeOptions = [5, 10, 20, 50, 100];
  searching: boolean = false;
  currentSearchTerm: string = '';
  currentUserSearchTerm: string = '';
  private userSearchSubject = new Subject<string>();

  constructor(
    private router: Router,
    private rolesService: RolesService,
    private userService: UserService,
    private toastr: ToastrService,
    private store: Store,
  ) { }

  openDialog(rolesData: any = null): void {
    const dialogRef = this.dialog.open(CreateRoleModal, {
      data: rolesData,
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.fetchRoles();
      }
    });
  }

  openPortalUserDialog(rolesData: any = null): void {
    const dialogRef = this.dialog.open(CreateProfileDialogModal, {
      data: rolesData,
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.loadUsers();
      }
    });
  }

  ngOnInit(): void {
    this.checkViewportSize();
    this.fetchRoles();
    this.loadUsers();
    this.checkPermissions();

  this.searchSubject
      .pipe(debounceTime(600), distinctUntilChanged())
      .subscribe((searchTerm) => {
        this.currentSearchTerm = searchTerm;
        this.fetchRoles(searchTerm, this.pageSize);
      });

    this.userSearchSubject
      .pipe(debounceTime(600), distinctUntilChanged())
      .subscribe((searchTerm) => {
        this.currentUserSearchTerm = searchTerm;
        this.loadUsers(searchTerm, this.userPageSize);
      });
  }

  checkPermissions() {
    this.store
      .select(selectPermissions.hasPermission('UPDATE_ROLE'))
      .subscribe((canEdit) => {
        this.store
          .select(selectPermissions.hasPermission('DELETE_ROLE'))
          .subscribe((canDelete) => {
            this.canEditOrDelete = canEdit || canDelete;
            this.updateColumnDefs();
          });
      });
  }

  onPortalUsersSearch(event: Event) {
    const input = event.target as HTMLInputElement;
    const searchTerm = input.value.trim();

    this.searching = searchTerm.length > 0;
    this.currentUserSearchTerm = searchTerm;
    this.userPage = 0;
    this.userSearchSubject.next(searchTerm);
  }

  checkViewportSize() {
    if (window.innerWidth > 1200) {
      this.viewPortSize = true;
    } else {
      this.viewPortSize = false;
    }
    this.updateColumnDefs();
    this.updateUserColumnDefs();
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    this.checkViewportSize();
  }

  editIconRenderer(params: any) {
    if (params.data?.role_key === 'super_admin') {
      return document.createTextNode('');
    }
    const icon = document.createElement('img');
    icon.src = 'assets/images/icons/icon-edit.svg';
    icon.alt = 'Edit';
    icon.style.width = '20px';
    icon.style.height = '20px';
    icon.style.cursor = 'pointer';
    icon.style.display = 'block';

    icon.addEventListener('click', (event: any) => {
      event.stopPropagation();
      this.openDialog(params.data);
    });
    return icon;
  }

  deleteIconRenderer(params: any) {
    if (params.data?.role_key === 'super_admin') {
      return document.createTextNode('');
    }
    const icon = document.createElement('img');
    icon.src = 'assets/images/icons/icon-delete.svg';
    icon.alt = 'Delete';
    icon.style.width = '20px';
    icon.style.height = '20px';
    icon.style.cursor = 'pointer';
    icon.style.display = 'block';
    icon.addEventListener('click', (event: any) => {
      event.stopPropagation();
      this.onDeleteRole(params?.data?.id);
    });
    return icon;
  }

  onDeleteRole(userId: number): void {
    const dialogRef = this.dialog.open(ConfirmDialog);
    dialogRef.afterClosed().subscribe((result) => {
      if (result === true) {
        this.deleteRole(userId);
      }
    });
  }

  viewUserDetails(params: any) {
    const button = document.createElement('button');
    button.textContent = 'View';
    button.classList.add('view-details-btn');
    button.addEventListener('click', () => {
    });

    return button;
  }

  roleNameRenderer(params: any) {
    const link = document.createElement('span');
    link.textContent = params.value;
    link.style.color = '#3473D1';
    link.style.cursor = 'pointer';
    link.style.fontWeight = '400';

    link.addEventListener('click', () => {
      const roleId = params.data.id;
      this.router.navigate([`roles/permissions/${roleId}`]);
    });

    return link;
  }

  updateUserColumnDefs(): void {
    this.userDefs = [
      {
        headerName: 'Profile Name',
        field: 'displayName',
        flex: this.viewPortSize ? 1 : 0,
        cellRenderer: this.profileNameRenderer.bind(this),
        cellStyle: { cursor: 'pointer' },
      },
      {
        headerName: 'Email',
        field: 'email',
        flex: this.viewPortSize ? 1 : 0,
      },
      {
        headerName: 'Role',
        field: 'role',
        flex: this.viewPortSize ? 1 : 0,
      },
      {
        headerName: 'Created On',
        field: 'createdAt',
        flex: this.viewPortSize ? 1 : 0,
      },
      {
        headerName: 'Actions',
        field: 'actions',
        flex: 0.5,
        minWidth: 120,
        floatingFilter: false,
        filter: false,
        sortable: false,
        cellRenderer: this.userIconRenderer.bind(this),
      },
    ];
  }

  userIconRenderer(params: any) {
    const div = document.createElement('div');
    div.style.display = 'flex';
    div.style.alignItems = 'center';
    div.style.gap = '20px';

    this.store.select(selectPermissions.hasPermission('UPDATE_USER')).subscribe((canEdit) => {
      if (canEdit) {
        const editIcon = document.createElement('img');
        editIcon.src = 'assets/images/icons/icon-edit.svg';
        editIcon.alt = 'Edit';
        editIcon.style.width = '20px';
        editIcon.style.height = '20px';
        editIcon.style.cursor = 'pointer';
        editIcon.style.display = 'block';
        editIcon.addEventListener('click', (event: any) => {
          event.stopPropagation();
          this.onEditUser(params.data);
        });
        div.appendChild(editIcon);
      }
    });

    this.store.select(selectPermissions.hasPermission('DELETE_USER')).subscribe((canDelete) => {
      if (canDelete) {
        const deleteIcon = document.createElement('img');
        deleteIcon.src = 'assets/images/icons/icon-delete.svg';
        deleteIcon.alt = 'Delete';
        deleteIcon.style.width = '20px';
        deleteIcon.style.height = '20px';
        deleteIcon.style.cursor = 'pointer';
        deleteIcon.style.display = 'block';
        deleteIcon.addEventListener('click', (event: any) => {
          event.stopPropagation();
          this.onDeleteUser(params.data);
        });
        div.appendChild(deleteIcon);
      }
    });

    return div;
  }


  profileNameRenderer(params: any) {
    const link = document.createElement('span');
    link.textContent = params.value;
    link.style.color = '#3473D1';
    link.style.cursor = 'pointer';
    link.style.fontWeight = '400';
    return link;
  }

  userActionRenderer(params: any) {
    const container = document.createElement('div');
    container.classList.add('action-btns');

    let canEdit = false;
    let canDelete = false;

    this.store.select(selectPermissions.hasPermission('UPDATE_USER'))
      .pipe(take(1))
      .subscribe(hasPermission => {
        canEdit = hasPermission;
      });

    this.store.select(selectPermissions.hasPermission('DELETE_USER'))
      .pipe(take(1))
      .subscribe(hasPermission => {
        canDelete = hasPermission;
      });

    if (canEdit) {
      const editBtn = document.createElement('button');
      editBtn.classList.add('edit-btn');
      editBtn.innerHTML = `<img src="assets/images/icons/edit.svg" alt="Edit" />`;
      editBtn.addEventListener('click', () => {
        this.onEditUser(params.data);
      });
      container.appendChild(editBtn);
    }

    if (canDelete) {
      const deleteBtn = document.createElement('button');
      deleteBtn.classList.add('delete-btn');
      deleteBtn.innerHTML = `<img src="assets/images/icons/delete.svg" alt="Delete" />`;
      deleteBtn.addEventListener('click', () => {
        this.onDeleteUser(params.data);
      });
      container.appendChild(deleteBtn);
    }

    return container;
  }

  updateColumnDefs(): void {
    this.columnDefs = [
      {
        headerName: 'Role',
        field: 'role',
        flex: this.viewPortSize ? 1 : 0,
        cellRenderer: this.roleNameRenderer.bind(this),
        cellStyle: { cursor: 'pointer' },
      },
      {
        headerName: 'Description',
        field: 'description',
        flex: this.viewPortSize ? 1 : 0,
      },
      {
        headerName: 'Created On',
        field: 'createdAt',
        flex: this.viewPortSize ? 1 : 0,
      },
    ];
    if (this.canEditOrDelete) {
      this.columnDefs.push({
        field: 'action',
        headerName: 'Action',
        flex: this.viewPortSize ? 1 : 0,
        floatingFilter: false,
        filter: false,
        sortable: false,
        cellRenderer: (params: any) => {
          const div = document.createElement('div');
          div.style.display = 'flex';
          div.style.alignItems = 'center';
          div.style.gap = '20px';

          this.store
            .select(selectPermissions.hasPermission('UPDATE_ROLE'))
            .subscribe((canEdit) => {
              if (canEdit) div.appendChild(this.editIconRenderer(params));
            });

          this.store
            .select(selectPermissions.hasPermission('DELETE_ROLE'))
            .subscribe((canDelete) => {
              if (canDelete) div.appendChild(this.deleteIconRenderer(params));
            });

          return div;
        },
      });
    }
  }

   onRolesSearch(event: Event) {
    const input = event.target as HTMLInputElement;
    const searchTerm = input.value.trim();

    this.searching = searchTerm.length > 0;
    this.currentSearchTerm = searchTerm;
    this.page = 0;
    this.searchSubject.next(searchTerm);
  }

  onRowclicked(event: any) {
    const roleId = event.data.id;
    const currentUserRole = sessionStorage.getItem('userRole');

    if (currentUserRole !== 'Super Admin') {
      this.toastr.error('Only Super Admin can update roles.');
      return;
    }

    this.store
      .select(selectPermissions.hasPermission('UPDATE_ROLE'))
      .pipe(take(1))
      .subscribe((canEdit) => {
        if (canEdit) {
          this.router.navigate([`roles/permissions/${roleId}`]);
        } else {
          this.toastr.warning('You do not have permission to edit roles.');
        }
      });
  }

  pageSize: number = 10;
  page: number = 0;
  totalPages = 1;
  pageSizeOptions = [5, 10, 20, 50, 100];

fetchRoles(search: string = '', limit: number = 10): void {
  if (limit) {
    this.pageSize = limit;
  }

  const offset = this.page * this.pageSize;

  this.rolesService.getRoles(search, limit, offset).subscribe({
    next: (res) => {
      this.totalPages = res.totalPages || 1;

      if (res.data.length === 0 && this.page > 0) {
        this.page = 0;
        this.fetchRoles(search, limit);
        return;
      }

      this.rowData = res.data.map((role: any) => ({
           id: role?.id,
          role: role?.name || '',
          name: role?.name || '',
          description: role?.description || '',
          createdAt: role?.createdAt
            ? new Date(role.createdAt).toLocaleDateString('en-GB')
            : '',
          role_key: role.key,
      }));
    },
    error: (err) => {
      console.error('Failed to load roles:', err);
    },
  });
}

 onPageSizeChange(event: Event): void {
    const newSize = Number((event.target as HTMLSelectElement).value);
    this.pageSize = newSize;
    this.page = 0;
    this.fetchRoles(this.currentSearchTerm, this.pageSize);
  }

 nextPage() {
    if (this.page + 1 < this.totalPages) {
      this.page++;
      this.fetchRoles(this.currentSearchTerm, this.pageSize);
    }
  }

  prevPage() {
    if (this.page > 0) {
      this.page--;
      this.fetchRoles(this.currentSearchTerm, this.pageSize);
    }
  }

  deleteRole(id: number): void {
    this.rolesService.deleteRole(id).subscribe({
      next: (res) => {
        this.toastr.success('Role deleted successfully');
        this.fetchRoles();
      },
      error: (err) => {
        console.error('Failed to delete role:', err);
      },
    });
  }

  deleteUser(id: number): void {
    this.userService.deleteUser(id).subscribe({
      next: (res) => {
        this.toastr.success('User deleted successfully');
        this.loadUsers();
      },
      error: (err) => {
        console.error('Failed to delete user:', err);
      },
    });
  }

loadUsers(search: string = '', limit: number = 10): void {
    const offset = this.userPage * this.userPageSize;

    this.userService.getUsers(search, this.userPageSize, offset).subscribe({
        next: (res) => {
            this.users = res.data.map((user: any) => ({
                id: user?.id,
                displayName: user.displayName || '',
                email: user.email || '',
                role: user?.role?.name || '',
                roleId: user?.role?.id || '',
                mobile: user.phoneNo || '',
                createdAt: user.createdAt
                    ? new Date(user.createdAt).toLocaleDateString('en-GB')
                    : '',
            }));

            this.userTotalPages = res.totalPages;

            if (this.userPage >= this.userTotalPages && this.userTotalPages > 0) {
                this.userPage = this.userTotalPages - 1;
            }
        },
        error: (err) => {
            console.error('Failed to load users:', err);
        },
    });
}

  onAddUser(): void {
    const dialogRef = this.dialog.open(CreateProfileDialogModal, {
      width: '400px',
      data: null,
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.loadUsers();
      }
    });
  }

  onEditUser(user: any): void {
    const dialogRef = this.dialog.open(CreateProfileDialogModal, {
      width: '400px',
      data: {
        id: user.id,
        email: user.email,
        displayName: user.displayName,
        roleId: user.roleId,
        role: user.role,
        profileName: user.displayName
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.loadUsers();
      }
    });
  }

  onDeleteUser(user: any): void {

    const dialogRef = this.dialog.open(ConfirmDialog);
    dialogRef.afterClosed().subscribe((result) => {
      if (result === true) {
        this.deleteUser(user.id);
      }
    });
  }

  fetchAllUsers(id: any): void {
    this.userService.getAllUsers(id).subscribe({
      next: (res) => {
        this.portalUsers = res;
      },
      error: (err) => {
        console.error('Failed to load child users:', err);
      },
    });
  }

  onUserPageSizeChange(event: Event): void {
    const newSize = Number((event.target as HTMLSelectElement).value);
    this.userPageSize = newSize;
    this.userPage = 0;
    this.loadUsers(this.currentUserSearchTerm, this.userPageSize);
  }

  nextUserPage() {
    if (this.userPage + 1 < this.userTotalPages) {
      this.userPage++;
      this.loadUsers(this.currentUserSearchTerm, this.userPageSize);
    }
  }

  prevUserPage() {
    if (this.userPage > 0) {
      this.userPage--;
      this.loadUsers(this.currentUserSearchTerm, this.userPageSize);
    }
  }
}

@Component({
  selector: 'dialog-modal',
  templateUrl: './modal/dialog-modal.html',
  styleUrl: './modal/dialog.component.scss',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogContent,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    NoOnlySpacesDirective,
    NoOnlySpecialAlphaCharsDirective,
  ],
})
export class CreateRoleModal {
  readonly dialogRef = inject(MatDialogRef<CreateRoleModal>);
  private rolesService = inject(RolesService);
  roles: any[] = [];
  isSubmitting = false;
  model: {
    name: '';
    description: string;
  } = {
      name: '',
      description: '',
    };

  constructor(
    private toastr: ToastrService,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) { }

  ngOnInit(): void {
    if (this.data?.id) {
      this.model = {
        name: this.data.name,
        description: this.data.description,
      };
    }
  }

  onNoClick(): void {
    this.dialogRef.close();
  }

  onSubmit(form: NgForm): void {
    if (!form.valid) {
      if (!this.model.name && !this.model.description) {
        this.toastr.error('Please fill in all required fields.');
        return;
      }
      if (!this.model.name) {
        this.toastr.error('Role name is required.');
      }
      if (!this.model.description) {
        this.toastr.error('Description is required.');
      }
      return;
    }
    this.isSubmitting = true;

    if (this.data?.id) {
      const updateData = {
        name: this.model.name,
        description: this.model.description,
      };
      this.rolesService.updateRole(this.data.id, updateData).subscribe({
        next: (res) => {
          this.isSubmitting = false;
          this.toastr.success(res?.message || 'Roles updated');
          this.dialogRef.close(res);
        },
        error: (err) => {
          this.isSubmitting = false;
          console.error('Failed to update role:', err);
        },
      });
    } else {
      this.rolesService.createRole(this.model).subscribe({
        next: (res) => {
          this.isSubmitting = false;
          this.toastr.success(res?.message || 'Role created');
          this.dialogRef.close(res);
        },
        error: (err) => {
          this.isSubmitting = false;
          console.error('Failed to create role:', err);
        },
      });
    }
  }
}

@Component({
  selector: 'app-profile',
  imports: [
    CommonModule,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    ReactiveFormsModule,
    MatSelectModule,
    MatDialogContent,
    FullscreenLoaderComponent,
  ],
  templateUrl: './user-dialog/user-dialog-modal.html',
  styleUrl: './user-dialog/user-dialog.component.scss',
})
export class CreateProfileDialogModal {
  @ViewChild('imgFileInput') imgFileInput!: ElementRef<HTMLInputElement>;
  readonly dialogRef = inject(MatDialogRef<CreateProfileDialogModal>);
  readonly dialog = inject(MatDialog);
  readonly storage = inject(Storage);
  currRole: any;
  roles: any;
  documentForm: FormGroup;
  currentPasswordHidden: boolean = true;
  newPasswordHidden: boolean = true;
  confirmPasswordHidden: boolean = true;
  rowData: any;
  file: File | null = null;
  fileName: any;
  url: string = '';
  uploading: boolean = false;
  loading: boolean = false;
  removeProfilePic: boolean = false;
  rolesDropdown: any[] = [];

  constructor(
    private userService: UserService,
    private authService: AuthService,
    private userProfileService: UserProfileService,
    private toastr: ToastrService,
    private injector: Injector,
    private cdr: ChangeDetectorRef,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    this.documentForm = new FormGroup({
      email: new FormControl('', [
        Validators.required,
        Validators.pattern(
          /^(?!.*\.\.)(?!\.)(?!.*\.$)[a-zA-Z0-9_%+-]+(\.[a-zA-Z0-9_%+-]+)*@[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)*\.[a-zA-Z]{2,}$/
        ),
      ]),
      roleId: new FormControl('', [Validators.required]),
      displayName: new FormControl('', [
        Validators.required,
        Validators.minLength(2),
        Validators.maxLength(60),
        Validators.pattern(/^[A-Za-z\s]+$/),
        noOnlySpacesValidator(),
      ]),
      password: new FormControl(
        '',
        this.conditionalValidator(
          Validators.pattern(
            /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z\d\s])[^\s]{6,}$/
          )
        )
      ),
      confirmPassword: new FormControl(
        '',
        this.conditionalValidator(Validators.pattern(/^.{6,}$/))
      ),
    });
    this.url = data?.profilePic;
  }

getRolesDropdown() {
  this.userService.getDropdownRoles().subscribe({
    next: (res) => {
      this.rolesDropdown = res?.data?.rows || [];

      if (this.data?.id) {
        this.documentForm.patchValue({
          roleId: this.data.roleId,
          email: this.data.email,
          displayName: this.data.profileName,
        });
      }

      this.currRole = this.authService.CURRENT_LOGGED_USER?.role.name;
      if (this.currRole !== 'Super Admin' && this.data?.id) {
        this.documentForm.get('roleId')?.disable();
      }
    },
    error: (err) => {
      console.error(err);
    },
  });
}

  ngOnInit(): void {
    this.getRolesDropdown();
    this.currRole = this.authService.CURRENT_LOGGED_USER?.role.name;
    if (this.currRole != 'Super Admin' && this.data?.id) {
      this.documentForm.get('roleId')?.disable();
    }
    if (!this.data?.id) {
      this.documentForm
        .get('password')
        ?.setValidators([
          Validators.required,
          Validators.pattern(
            /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z\d\s])[^\s]{6,}$/
          ),
        ]);
      this.documentForm
        .get('confirmPassword')
        ?.setValidators([Validators.required, Validators.pattern(/^.{6,}$/)]);
      this.documentForm.get('password')?.updateValueAndValidity();
      this.documentForm.get('confirmPassword')?.updateValueAndValidity();
    } else {
      this.documentForm.get('email')?.disable();
    }
  }

  getDisplayNameErrors(): string {
    const control = this.documentForm.get('displayName');
    if (!control || !control.errors || !control.touched) return '';

    if (control.errors['required']) {
      return 'Name is required';
    }
    if (control.errors['minlength']) {
      return `Name must be at least ${control.errors['minlength'].requiredLength} characters`;
    }
    if (control.errors['maxlength']) {
      return `Name cannot exceed ${control.errors['maxlength'].requiredLength} characters`;
    }
    if (control.errors['pattern']) {
      return 'Name can only contain letters and spaces';
    }
    if (control.errors['noOnlySpaces']) {
      return 'Name cannot be empty';
    }
    return '';
  }
  conditionalValidator(validator: ValidatorFn): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (value && value.length > 0) {
        return validator(control);
      }
      return null;
    };
  }

  onNoClick(): void {
    this.dialogRef.close();
  }

  onRemoveProfile() {
    this.removeProfilePic = true;
    this.url = '';
    if (this.imgFileInput) {
      this.imgFileInput.nativeElement.value = '';
    }
  }

  onHidePassword(field: number) {
    field === 1
      ? (this.currentPasswordHidden = !this.currentPasswordHidden)
      : field === 2
        ? (this.newPasswordHidden = !this.newPasswordHidden)
        : (this.confirmPasswordHidden = !this.confirmPasswordHidden);
  }

  passwordPermission() {
    if (this.data.role) {
      if (this.data.role === 'Super Admin') {
        return true;
      } else {
        if (!this.data.id) {
          return true;
        } else {
          return false;
        }
      }
    } else {
      return true;
    }
  }

  onSave() {
    if (this.documentForm.invalid) {
      this.documentForm.markAllAsTouched();
      return;
    }
    const payload = {
      email: this.documentForm.get('email')?.value.toLowerCase(),
      displayName: this.documentForm.value.displayName,
      roleId: this.documentForm.value.roleId,
      ...(this.documentForm.value.password.length > 0 && {
        password: this.documentForm.value.password,
      }),
      ...(this.documentForm.value.confirmPassword.length > 0 && {
        confirmPassword: this.documentForm.value.confirmPassword,
      }),
      profilePic: this.removeProfilePic ? '' : this.url,
    };
    this.loading = true;
    if (this.data?.id) {
      this.userService.updateUser(this.data?.id, payload).subscribe({
        next: (res) => {
          this.toastr.success(res?.message);
          this.userProfileService.triggerProfileRefresh();
          this.dialogRef.close(res);
          this.loading = false;
        },
        error: (err) => {
          console.error('error:', err);
          this.loading = false;
        },
      });
    } else {
      this.userService.createUser(payload).subscribe({
        next: (res) => {
          this.userProfileService.triggerProfileRefresh();
          this.toastr.success(res?.message);
          this.dialogRef.close(res);
          this.loading = false;
        },
        error: (err) => {
          if (err.status != 409) {
            this.toastr.error(err.message);
          }
          this.loading = false;
        },
      });
    }
  }

  onCancel() {
    this.onNoClick();
  }

  async onFileSelected(event: Event) {
    this.removeProfilePic = false;
    const input = event?.target as HTMLInputElement;
    if (input.files) {
      this.file = input?.files[0];
      if (!this.file) {
        return;
      }
      const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg'];
      if (allowedTypes.includes(this.file?.type)) {
        const filePath = `profile_images/${this.file?.name}_${Date.now()}`;
        const fileRef = ref(this.storage, filePath);
        await runInInjectionContext(this.injector, async () => {
          try {
            this.uploading = true;
            if (this.file) {
              await uploadBytes(fileRef, this.file);
              this.url = await getDownloadURL(fileRef);
              this.fileName = this.file?.name;
            }
          } catch (err) {
            this.toastr.error(
              'Upload failed due to an internal error. Please try again later.'
            );
          } finally {
            this.uploading = false;
            this.cdr.detectChanges();
          }
        });
      } else {
        if (this.imgFileInput) {
          this.imgFileInput.nativeElement.value = '';
        }
        this.toastr.error('Unsupported file format');
      }
    }
  }
}
